/**
 * Initialen und eine feste Farbe je Person — für Avatare ohne hochgeladenes Bild.
 *
 * Beides sind reine Funktionen und stehen deshalb hier, nicht in der Komponente:
 * „AB" für „Anna Beck" und „welche Farbe" lassen sich prüfen, ohne etwas zu rendern.
 *
 * Die Farbe kommt aus den Status-Tönen des Designsystems (`Tone`), damit auch die
 * Avatare in derselben Farbsprache sprechen wie der Rest — kein einziger Farbwert
 * im Code.
 */

import type { Tone } from './statusTones';

/**
 * Bis zu zwei Großbuchstaben als Initialen.
 *
 * Ein Wort → sein erster Buchstabe; mehrere → erster und letzter (so wird aus
 * „Anna Maria Beck" „AB", nicht „AM"). Fällt kein Buchstabe an, ein neutrales „?",
 * damit der Kreis nie leer bleibt.
 */
export function initialen(name: string): string {
  const woerter = name.trim().split(/\s+/).filter(Boolean);
  if (woerter.length === 0) return '?';

  const erster = woerter[0]![0]!;
  const letzter = woerter.length > 1 ? woerter[woerter.length - 1]![0]! : '';
  return (erster + letzter).toUpperCase();
}

/**
 * Die Töne, aus denen Avatare gefärbt werden — bewusst OHNE Rot (liest sich als
 * Fehler) und Grau (zu blass, um Personen zu unterscheiden). Die Auswahl ist fest
 * und geordnet: derselbe Schlüssel ergibt immer dieselbe Farbe.
 */
const AVATAR_TOENE: Tone[] = ['blue', 'green', 'purple', 'orange', 'amber'];

/**
 * Ein fester Ton je Schlüssel (Benutzer-ID oder, ersatzweise, der Name).
 *
 * An die ID gebunden, nicht an den Namen: Zwei Personen dürfen gleich heißen und
 * bekommen trotzdem verschiedene Farben, und ein umbenannter Mensch behält seine.
 */
export function avatarTon(schluessel: string): Tone {
  let summe = 0;
  for (let i = 0; i < schluessel.length; i += 1) {
    summe = (summe + schluessel.charCodeAt(i)) % 997;
  }
  return AVATAR_TOENE[summe % AVATAR_TOENE.length]!;
}
